import { connectOperationQueue } from "./serial.ts";
import { connectAccounts, readConnectProfile, withConnectAccountToken } from "./accounts.ts";
import type { ConnectDesktopBridge, FeedbackBridge } from "@afar-research/contracts";
import { connectClient, signOutConnect } from "./index.ts";
import { submitFeedbackDraft } from "./feedback.ts";
import { registerConnectInstallation } from "./installation.ts";
import { cloudWorkspaceConnectOutcome } from "./cloudWorkspaceConnection.ts";
import {
  networkClient,
  networkConnectTarget,
  networkMachineRoutes,
  openNetworkConnection,
} from "./networkConnection.ts";
import { ConnectError, type ConnectFetch } from "@afar-research/shared/connect";
import type { ConnectTokens } from "./signIn.ts";

export const browserConnectStorageKey = (connectOrigin: string) =>
  `afar-connect:installation:v1:${connectOrigin}`;

export function makeBrowserConnect(input: {
  /** Connect's origin, which is also its OAuth issuer (ADR-0132). */
  connectOrigin: string;
  /** Shown to the person on the destination when they choose which client to revoke. */
  clientLabel: string;
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  fetch: ConnectFetch;
  signIn: () => Promise<ConnectTokens>;
  /** Renews a saved access token, so a paired browser outlives its grant the same way (#1008). */
  renewal?: {
    refresh: (refreshToken: string) => Promise<ConnectTokens>;
    now: () => Promise<number>;
  };
  disconnect: (environmentId?: string) => void;
  /** What a Feedback Issue records about the client that filed it. */
  feedbackClient: { clientVersion: string; platform: string };
}): ConnectDesktopBridge & { invalidateConnections(): Promise<void>; feedback: FeedbackBridge } {
  const key = browserConnectStorageKey(input.connectOrigin);
  const accounts = connectAccounts({
    read: async () => input.storage.getItem(key) ?? undefined,
    write: async (value) => input.storage.setItem(key, value),
    ...(input.renewal ? { renewal: input.renewal } : {}),
    changed: () => {
      input.disconnect();
      return Promise.resolve();
    },
  });
  const load = () => accounts.load();
  const client = connectClient(input);
  const network = networkClient(input);
  const machineRoutes = networkMachineRoutes(input.storage, input.connectOrigin);
  const clearCredential = () => accounts.removeActive();
  const serialize = connectOperationQueue();
  /**
   * Runs a call with a token that renews itself. Custody serializes its own read-modify-write, so
   * this needs no queue of its own and stays callable from inside one.
   */
  const withToken = <A>(operation: (token: string) => Promise<A>) =>
    withConnectAccountToken(accounts, operation);
  const signOut = async () =>
    signOutConnect({
      ...input,
      credential: (await load()).installation.credential,
      clearCredential,
    });
  return {
    contributionProject: (projectId) =>
      serialize(() => withToken((token) => client.contributionProject(token, projectId))),
    contributionProjects: () =>
      serialize(() => withToken((token) => client.contributionProjects(token))),
    createContributionProject: (request) =>
      serialize(() => withToken((token) => client.createContributionProject(token, request))),
    publishProjectContribution: (projectId, attemptId, snapshot) =>
      serialize(() =>
        withToken((token) =>
          client.publishProjectContribution(token, projectId, attemptId, snapshot),
        ),
      ),
    withdrawProjectContribution: (projectId, snapshotId) =>
      serialize(() =>
        withToken((token) => client.withdrawProjectContribution(token, projectId, snapshotId)),
      ),
    cloudAllowance: () => serialize(() => withToken((token) => client.cloudAllowance(token))),
    cloudAccounts: (search) =>
      serialize(() => withToken((token) => client.cloudAccounts(token, search))),
    cloudAllowanceHistory: (accountId) =>
      serialize(() => withToken((token) => client.cloudAllowanceHistory(token, accountId))),
    adjustCloudAllowance: (request) =>
      serialize(() => withToken((token) => client.adjustCloudAllowance(token, request))),
    changeCloudSubscription: (request) =>
      serialize(() => withToken((token) => client.changeCloudSubscription(token, request))),
    cloudWorkspaces: () => serialize(() => withToken((token) => client.cloudWorkspaces(token))),
    createCloudWorkspace: (request) =>
      serialize(() => withToken((token) => client.createCloudWorkspace(token, request))),
    cloudWorkspaceAction: (request) =>
      serialize(async () => {
        await withToken((token) => client.cloudWorkspaceAction(token, request));
      }),
    connectEnvironment: (request) =>
      serialize(async () => {
        const installation = (await load()).installation;
        // Every Connect Environment is re-introduced through its network on every attempt: its
        // route ticket is short-lived, and that is when Connect re-checks access (and starts a
        // stopped Cloud Workspace).
        const target = networkConnectTarget(
          request.environmentId,
          machineRoutes.get(request.environmentId),
        );
        return cloudWorkspaceConnectOutcome(
          async () =>
            (
              await openNetworkConnection({
                fetch: input.fetch,
                connectOrigin: input.connectOrigin,
                installation,
                clientLabel: input.clientLabel,
                target,
              })
            ).endpoint,
        );
      }),
    networks: {
      networks: () => serialize(() => withToken((token) => network.networks(token))),
      createEnrollmentCode: (networkId) =>
        serialize(() => withToken((token) => network.createEnrollmentCode(token, networkId))),
      guestProvisioning: (networkId, parentMachineId, request) =>
        serialize(() =>
          withToken((token) =>
            network.guestProvisioning(token, networkId, parentMachineId, request),
          ),
        ),
      guestAction: (networkId, parentMachineId, request) =>
        serialize(() =>
          withToken((token) => network.guestAction(token, networkId, parentMachineId, request)),
        ),
      networkRemovalImpact: (networkId, machineId) =>
        serialize(() => withToken((token) => network.removalImpact(token, networkId, machineId))),
      removeNetworkMachine: async (networkId, machineId) => {
        await serialize(() =>
          withToken((token) => network.removeMachine(token, networkId, machineId)),
        );
      },
      setNetworkMachineEnabled: async (networkId, machineId, enabled) => {
        await serialize(() =>
          withToken((token) => network.setMachineEnabled(token, networkId, machineId, enabled)),
        );
      },
      setUpMachineOverSsh: (networkId, setup) =>
        serialize(() => withToken((token) => network.setUpOverSsh(token, networkId, setup))),
      setMemberEnvironmentLimits: async (networkId, machineId, limits) => {
        await serialize(() =>
          withToken((token) => network.setLimits(token, networkId, machineId, limits)),
        );
      },
      memberEnvironmentAction: async (networkId, machineId, accountId, action) => {
        await serialize(() =>
          withToken((token) =>
            network.memberEnvironmentAction(token, networkId, machineId, accountId, action),
          ),
        );
      },
      connectNetworkMachine: (request) =>
        serialize(async () => {
          const installation = (await load()).installation;
          try {
            const opened = await openNetworkConnection({
              fetch: input.fetch,
              connectOrigin: input.connectOrigin,
              installation,
              clientLabel: input.clientLabel,
              target: request,
            });
            machineRoutes.remember(opened.environmentId, { ...request, label: opened.label });
            return {
              _tag: "NetworkMachineConnected" as const,
              environmentId: opened.environmentId,
              label: opened.label,
            };
          } catch (cause) {
            if (!(cause instanceof ConnectError)) throw cause;
            return {
              _tag: "NetworkMachineRefused" as const,
              code: cause.code,
              status: cause.status,
            };
          }
        }),
      rememberedMachines: () => machineRoutes.list(),
      forgetNetworkMachine: (environmentId) => machineRoutes.forget(environmentId),
      createOrganization: (name) =>
        serialize(() => withToken((token) => network.createOrganization(token, name))),
      organization: (organizationId) =>
        serialize(() => withToken((token) => network.organization(token, organizationId))),
      inviteToOrganization: (organizationId, email, role) =>
        serialize(() => withToken((token) => network.invite(token, organizationId, email, role))),
      revokeOrganizationInvitation: async (organizationId, invitationId) => {
        await serialize(() =>
          withToken((token) => network.revokeInvitation(token, organizationId, invitationId)),
        );
      },
      acceptOrganizationInvitation: (invitation) =>
        serialize(() => withToken((token) => network.acceptInvitation(token, invitation))),
      changeOrganizationRole: async (organizationId, accountId, role) => {
        await serialize(() =>
          withToken((token) => network.changeRole(token, organizationId, accountId, role)),
        );
      },
      removeOrganizationMember: async (organizationId, accountId) => {
        await serialize(() =>
          withToken((token) => network.removeMember(token, organizationId, accountId)),
        );
      },
      deleteOrganization: async (organizationId) => {
        await serialize(() =>
          withToken((token) => network.deleteOrganization(token, organizationId)),
        );
      },
    },
    getStatus: () =>
      serialize(async () => {
        try {
          if (!input.storage.getItem(key)) return { state: "signed-out" as const };
          return await accounts.status();
        } catch {
          return {
            state: "signed-out" as const,
            message: "Your saved Connect account could not be restored. Sign in again.",
          };
        }
      }),
    invalidateConnections: () => {
      input.disconnect();
      return Promise.resolve();
    },
    signIn: () => {
      // Start during the click, before network requests can lose browser popup permission.
      const account = input.signIn();
      void account.catch(() => undefined);
      return serialize(async () => {
        const tokens = await account;
        const profile = await readConnectProfile(
          input.fetch,
          input.connectOrigin,
          tokens.accessToken,
        );
        await accounts.add({
          tokens,
          profile,
          isRegistered: async (id) =>
            (await client.installations(tokens.accessToken)).some(
              (item) => item.installationId === id,
            ),
          register: () =>
            registerConnectInstallation(input.fetch, input.connectOrigin, tokens.accessToken),
          revoke: (credential) =>
            signOutConnect({ ...input, credential, clearCredential: async () => {} }),
        });
      });
    },
    switchAccount: (id) => serialize(() => accounts.switchAccount(id)),
    feedback: {
      submit: (draft) =>
        submitFeedbackDraft({
          ...input.feedbackClient,
          draft,
          submit: (submission) =>
            serialize(() => withToken((token) => client.submitFeedback(token, submission))),
        }),
    },
    signOut: () => serialize(signOut),
    installations: () => serialize(() => withToken((token) => client.installations(token))),
    revokeInstallation: (id) =>
      // The token is taken inside the queue with everything else it must agree with: reading it
      // first would let an account switch land in between and revoke under the wrong account.
      serialize(async () => {
        const credential = await load();
        await withToken((token) => client.revokeInstallation(token, id));
        if (id === credential.installation.installationId) await clearCredential();
      }),
  };
}
